import React, { Component } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { Link } from 'react-router-dom';
import { Grid } from 'react-bootstrap';
import { debounce, get } from 'lodash';
import moment from 'moment';
import fastMemoize from 'fast-memoize';
import ButtonStandardPrimary from 'components/atoms/buttons/standard/Primary';
import { LoadingTile } from 'components/molecules/ErrorTile';
import { fetchList } from 'services/brands/duck';
import DebounceInputChange from 'hoc/DebounceInputChange';
import StyledReactTable from 'components/molecules/StyledReactTable';
import Pagination from 'components/molecules/tables/components/Pagination';
import CellDiv from 'components/molecules/tables/components/CellDiv';

const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 25;

const DebouncedInput = DebounceInputChange(500)(props => <input {...props} />);

const generateFilterValue = (filter, row) => row[filter.id];
const memoizeFilterValue = fastMemoize(generateFilterValue);

class BrandsTableContainer extends Component {
    constructor(props) {
        super(props);

        this.activeSearch = false;
        this.state = {
            showPagination: false,

            currentUrl: props.parsedUrl.href,
            list: null,

            // controlled table
            // filtered: [],
            // sorted: [],
            // page: 0,
            // pageSize: 25
            ...BrandsTableContainer.convertUriToState(props.parsedUrl)
        };

        this.brandTableInstance = React.createRef();
        this.mirrorStateToUri = debounce(this.mirrorStateToUri.bind(this), 500);
        this.getColumns = this.getColumns.bind(this);
        this.setActiveSearch = this.setActiveSearch.bind(this);
    }

    componentDidMount() {
        this.props.fetchList();
    }

    shouldComponentUpdate() {
        return !this.activeSearch;
    }

    setActiveSearch(value) {
        this.activeSearch = value;
    }


    getColumns() {
        return [
            {
                Header: '',
                Cell: props => (
                    <React.Fragment>
                        {this.linkToEditBrand(null, props.original)}
                    </React.Fragment>
                ),
                maxWidth: 30,
                resizable: false,
                sortable: false
            }, {
                Header: 'NAME',
                Cell: props => this.nameLink(null, props.original),
                accessor: 'name',
                filterable: true,
                Filter: ({ filter, onChange }) => (
                    <DebouncedInput
                        type="text"
                        className="filter text-filter form-control brandName-filter"
                        onChange={value => onChange(value)}
                        onFocus={() => this.setActiveSearch(true)}
                        onBlur={() => this.setActiveSearch(false)}
                        placeholder="Brand Name"
                        style={{ height: 38, borderColor: 'hsl(0,0%,80%)' }}
                        value={filter ? filter.value : ''} />
                ),
                filterMethod: (filter, row) => {
                    const filterValue = memoizeFilterValue(filter, row);

                    return filterValue.toLowerCase().includes(filter.value.toLowerCase());
                }
            }, {
                Header: 'CREATED',
                accessor: 'createdAt',
                Cell: props => <CellDiv value={this.dateFormatter(props.value)} type="date" style={{ textAlign: 'right' }} />,
                maxWidth: 150
            }
        ];
    }

    static getDerivedStateFromProps(props, state) {
        const propList = get(props, 'brands.list', []);
        const propUrl = get(props, 'parsedUrl', {});
        const mergeList = [];

        if (propList !== state.list) {
            let showPagination = state.showPagination;

            if (!state.showPagination && propList.length > DEFAULT_PAGE_SIZE) {
                showPagination = true;
            }

            mergeList.push({
                list: propList,
                showPagination
            });
        }

        if (state.currentUrl !== propUrl.href) {
            mergeList.push({
                currentUrl: propUrl.href,
                ...BrandsTableContainer.convertUriToState(propUrl)
            });
        }

        if (mergeList.length) {
            return Object.assign.apply(null, [
                {},
                state
            ].concat(mergeList));
        }
        return null;
    }

    getBrandTableInstance() {
        if (!this.brandTableInstance) {
            console.warn('React Table - No instance'); // eslint-disable-line
        }

        return this.brandTableInstance;
    }

    onFilteredChangeCustom(value, accessor) {
        let filtered = this.state.filtered;

        const tempFilters = filtered.reduce((prev, filter) => {
            prev[filter.id] = filter.value;
            return prev;
        }, {});

        tempFilters[accessor] = value;

        filtered = Object.keys(tempFilters).map((key) => {
            if (tempFilters[key] === null) {
                return false;
            }
            return { id: key, value: tempFilters[key] };
        }).filter(item => item);

        const instance = this.getBrandTableInstance().current;
        let page = this.state.page;
        if (get(instance, 'state.sortedData', []).length < this.state.page * this.state.pageSize) {
            page = DEFAULT_PAGE;
        }

        this.setState({ filtered, page }, () => this.mirrorStateToUri());
    }

    dateFormatter(cell) {
        return `${moment(cell).format('MM-DD-YYYY')}`;
    }

    linkToEditBrand(cell, row) {
        return (
            <Link key={0} title="Edit Brand" to={`/admin/brands/${row.id}/edit`} >
                <span className="icon-edit" />
            </Link>
        );
    }

    nameLink(cell, row) {
        return (
            <Link title="View Brand" to={`/admin/brands/${row.id}`}>
                {row.name}
            </Link>
        );
    }

    mirrorStateToUri() {
        const query = {};

        this.state.filtered.forEach((filter) => {
            switch (filter.id) {
            case 'name':
                if (filter.value) {
                    query.brandName = filter.value;
                }
                break;

            default:
                // ignore
            }
        });

        if (this.state.page !== DEFAULT_PAGE) {
            query.page = this.state.page;
        }

        if (this.state.pageSize !== DEFAULT_PAGE_SIZE) {
            query.pageSize = this.state.pageSize;
        }

        if (this.state.sorted.length) {
            const sortedItem = this.state.sorted[0];

            query.sort = sortedItem.id;
            query.sortDir = sortedItem.desc ? 'desc' : 'asc';
        }

        this.props.parsedUrl.set('query', query);
        this.props.push(this.props.parsedUrl.toString({ onlyPath: true }));
    }

    static convertUriToState(url) {
        const filtered = [];
        const sorted = [];
        const { query } = url;

        if (query.brandName) {
            filtered.push({ id: 'name', value: query.brandName });
        }

        if (query.sort) {
            sorted.push({ id: query.sort, desc: query.sortDir === 'desc' });
        }

        let page = Number(query.page);
        if (!page || page < 0) {
            page = DEFAULT_PAGE;
        }

        let pageSize = Number(query.pageSize);
        if (!pageSize || pageSize < 1) {
            pageSize = DEFAULT_PAGE_SIZE;
        }

        return {
            filtered,
            sorted,
            page,
            pageSize
        };
    }

    render() {
        const data = this.props.brands.list;
        const dataFetched = this.props.brands.listFetched;

        return (
            <Grid fluid>
                <ButtonStandardPrimary
                    type="submit"
                    onClick={() => this.props.push('/admin/brands/create')}>
                            Create Brand
                </ButtonStandardPrimary>
                <br />
                <br />
                {dataFetched ? (
                    <React.Fragment>
                        <StyledReactTable
                            data={data}
                            ref={this.brandTableInstance}
                            defaultPageSize={25}
                            minRows={10}
                            filtered={this.state.filtered}
                            sorted={this.state.sorted}
                            page={this.state.page}
                            pageSize={this.state.pageSize}
                            onFilteredChange={(filtered, column, value) => {
                                this.onFilteredChangeCustom(value, column.id || column.accessor);
                            }}
                            onSortedChange={sorted => this.setState({ sorted }, () => this.mirrorStateToUri())}
                            onPageChange={page => this.setState({ page }, () => this.mirrorStateToUri())}
                            onPageSizeChange={(pageSize, page) => this.setState({
                                page, pageSize
                            }, () => this.mirrorStateToUri())}
                            getTheadThProps={() => ({
                                style: {
                                    backgroundColor: '#E2E2E2',
                                }
                            })}
                            getTheadFilterThProps={() => ({
                                style: {
                                    overflow: 'inherit' // allows react-select to work
                                }
                            })}
                            columns={this.getColumns()}
                            defaultSorted={[{
                                id: 'name', desc: false
                            }]}
                            className="-highlight"
                            showPagination={this.state.showPagination}
                            PaginationComponent={Pagination}
                        />
                    </React.Fragment>
                ) : (
                    <LoadingTile />
                )}
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    brands: state.brands,
    parsedUrl: state.router.parsedUrl
});

export default connect(mapStateToProps, { fetchList, push })(BrandsTableContainer);



// WEBPACK FOOTER //
// ./src/containers/tables/BrandsTableContainer.js